import { Post } from "../../types/api/Post";
import { format } from "date-fns";
import { getSignedUrl } from "../../api/files";
import { useState, useEffect, useRef, useCallback } from "react";
import { Calendar } from "react-feather";
import { formatLocation } from "../../shared/formatLocation";
import { Location } from "../../types/api/Location";
import { getVideoFromThumbnailUrl, isVideo } from "../../utils/videos";

interface CalendarGridProps {
  posts: Post[];
  onPostClick?: (post: Post) => void;
}

const POSTS_PER_PAGE = 10;

export function CalendarFeed({ posts, onPostClick }: CalendarGridProps) {
  const [signedUrls, setSignedUrls] = useState<Record<string, string>>({});
  const [displayedCount, setDisplayedCount] = useState(POSTS_PER_PAGE);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  const sortedPosts = [...posts].sort((a, b) => {
    return (
      new Date(b.publish_at!).getTime() - new Date(a.publish_at!).getTime()
    );
  });

  console.log("Displayed count:", displayedCount, sortedPosts.length);

  const displayedPosts = sortedPosts.slice(0, displayedCount);

  const loadMore = useCallback(() => {
    setDisplayedCount((prev) => {
      if (prev < sortedPosts.length) {
        return Math.min(prev + POSTS_PER_PAGE, sortedPosts.length);
      }
      return prev;
    });
  }, [sortedPosts.length]);

  useEffect(() => {
    const loadSignedUrls = async () => {
      const urls: Record<string, string> = {};

      for (const post of displayedPosts) {
        for (const media of post.posts_media || []) {
          if (media.src_960 && media.src_thumbnail) {
            try {
              if (isVideo(media)) {
                const url = await getSignedUrl(
                  getVideoFromThumbnailUrl(media.src_thumbnail)
                );
                urls[media.src_thumbnail] = url;
              } else {
                const url = await getSignedUrl(media.src_960);
                urls[media.src_960] = url;
              }
            } catch (e) {
              const url = await getSignedUrl(media.src_thumbnail);
              urls[media.src_thumbnail] = url;
            }
          }
        }
      }

      setSignedUrls(urls);
    };

    loadSignedUrls();
  }, [displayedPosts.length]);

  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [loadMore]);

  useEffect(() => {
    setDisplayedCount(POSTS_PER_PAGE);
  }, [posts]);

  const handlePhotoClick = (post: Post) => {
    if (onPostClick) {
      onPostClick(post);
    }
  };

  return (
    <>
      <div className="max-w-xl mx-auto space-y-4">
        {displayedPosts.map((post) => {
          const media = post.posts_media || [];
          if (media.length === 0) return null;

          return (
            <div
              key={post._id}
              onClick={() => handlePhotoClick(post)}
              className="card rounded-md bg-base-100 shadow-sm hover:shadow-lg transition-shadow cursor-pointer"
            >
              <div className="card-body gap-0 p-0">
                <div className="flex items-center gap-2 p-4">
                  <Calendar size={16} />
                  {post.publish_at &&
                    format(new Date(post.publish_at), "MMM d, yyyy")}
                </div>

                {media.length > 1 && (
                  <div className="carousel carousel-center bg-base-200">
                    {media.map((m) => (
                      <div key={m.id} className="carousel-item">
                        <img
                          className="max-w-xl h-auto object-contain"
                          src={signedUrls[m?.src_960!]}
                          alt={post.posts_media?.[0]?.title || ""}
                        />
                      </div>
                    ))}
                  </div>
                )}
                {media.length === 1 && !isVideo(media[0]) && (
                  <img
                    className="object-cover w-full"
                    src={signedUrls[media[0]?.src_960!]}
                    alt={post.posts_media?.[0]?.title || ""}
                  />
                )}
                {media.length === 1 && isVideo(media[0]) && (
                  <video
                    className="object-cover w-full"
                    src={signedUrls[media[0]?.src_thumbnail!]}
                    controls
                  />
                )}

                <div className="space-y-4 p-4">
                  {post.posts_for_feeds?.[0]?.location && (
                    <p>
                      {formatLocation(
                        post.posts_for_feeds?.[0]
                          ?.location as unknown as Location,
                        true
                      )}
                    </p>
                  )}

                  <p>{post.posts_for_feeds?.[0]?.content}</p>
                </div>
              </div>
            </div>
          );
        })}

        {displayedCount < sortedPosts.length && (
          <div ref={loadMoreRef} className="py-4 text-center">
            <span className="loading loading-spinner loading-md"></span>
          </div>
        )}

        <div className="h-48 w-full"></div>
      </div>
    </>
  );
}
